function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}
var dispatchCustomEvent = function dispatchCustomEvent(eventName) {
  var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var detail = {
    detail: data
  };
  var event = new CustomEvent(eventName, data ? detail : null);
  document.dispatchEvent(event);
};
window.recentlyViewedIds = [];

/**
 *  @class
 *  @function Quantity
 */
if (!customElements.get('quantity-selector')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.qty');
      this.step = this.input.getAttribute('step');
      this.changeEvent = new Event('change', {
        bubbles: true
      });
      // Create buttons
      this.subtract = this.querySelector('.minus');
      this.add = this.querySelector('.plus');

      // Add functionality to buttons
      this.subtract.addEventListener('click', () => this.change_quantity(-1 * this.step));
      this.add.addEventListener('click', () => this.change_quantity(1 * this.step));
      this.input.addEventListener("change", () => this.change_quantity)

    }
    connectedCallback() {
      this.classList.add('buttons_added');
      this.validateQtyRules();
    }
    change_quantity(change) {
      // Get current value
      let quantity = Number(this.input.value);

      // Ensure quantity is a valid number
      if (isNaN(quantity)) quantity = 1;

      // Check for min & max
      if (this.input.getAttribute('min') > (quantity + change)) {
        return;
      }
      if (this.input.getAttribute('max')) {
        if (this.input.getAttribute('max') < (quantity + change)) {
          return;
        }
      }
      // Change quantity
      quantity += change;

      // Ensure quantity is always a number
      quantity = Math.max(quantity, 1);

      // Output number
      this.input.value = quantity;

      this.input.dispatchEvent(this.changeEvent);

      this.validateQtyRules();
    }
    validateQtyRules() {
      const value = parseInt(this.input.value);
      if (this.input.min) {
        const min = parseInt(this.input.min);
        this.subtract.classList.toggle('disabled', value <= min);
      }
      if (this.input.max) {
        const max = parseInt(this.input.max);
        this.add.classList.toggle('disabled', value >= max);
      }
    }
  }
  customElements.define('quantity-selector', QuantityInput);
}

if (!customElements.get('cart-quantity-selector')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.changeEvent = new Event('change', { bubbles: true });

      // Check if this is a dropdown style (cart page)
      this.isDropdown = this.classList.contains('dropdown-style');

      if (this.isDropdown) {
        this.initDropdown();
      } else {
        this.initButtons();
      }
    }

     initDropdown() {
      this.select = this.querySelector('.qty-select');
      if (this.select) {
        this.select.addEventListener('change', (e) => {
          this.handleDropdownChange(e);
        });

        const wrapper = this.querySelector('.qty-dropdown-wrapper');
        if (wrapper) {
          wrapper.addEventListener('click', (e) => {
            if (e.target === this.select) return;
            e.preventDefault();
            this.select.focus();
            if (typeof this.select.showPicker === 'function') {
              try {
                this.select.showPicker();
              } catch (err) {
              }
            }
          });
        }
      }
    }


    async handleDropdownChange(e) {
      e.stopPropagation();
      const requestedQuantity = parseInt(e.target.value);
      const key = e.target.dataset.key;

      // Store original value for potential rollback
      const originalValue = e.target.dataset.originalValue || e.target.value;
      e.target.dataset.originalValue = e.target.value;

      await this.updateCartQuantity(key, requestedQuantity, originalValue);
    }

    initButtons() {
      this.input = this.querySelector('.qty');
      this.step = parseInt(this.input.getAttribute('step')) || 1;
      this.subtract = this.querySelector('.minus');
      this.add = this.querySelector('.plus');

      this.subtract.addEventListener('click', () => this.change_quantity(-1 * this.step));
      this.add.addEventListener('click', () => this.change_quantity(1 * this.step));
      this.input.addEventListener("change", () => this.input.dispatchEvent(this.changeEvent));
    }

    connectedCallback() {
      if (!this.isDropdown) {
        this.classList.add('buttons_added');
        this.validateQtyRules();
      }
    }

    async updateCartQuantity(key, requestedQuantity, previousValue) {
      try {
        document.querySelector('.thb-cart-form')?.classList.add('loading');

        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            updates: { [key]: requestedQuantity },
            sections: this.getSectionsToRender().map(section => section.section),
            sections_url: window.location.pathname
          })
        });

        const state = await response.json();

        // CRITICAL: Check for status code 422 - Shopify inventory limit response
        if (state.status === 422) {
          await this.handleInventoryLimitFromResponse(state, key, requestedQuantity);
          return;
        }

        // Check for general errors
        if (state.errors) {
          this.handleUpdateError(state.errors, previousValue);
          return;
        }

        // Success case - Find the cart item
        const cartItem = state.items?.find(item => item.key === key);
        
        if (!cartItem) {
          this.replaceCartContent(state);
          this.showMessage('error', window.theme.cartToastStrings.quantityItemNotFound);
          return;
        }

        const actualQuantity = cartItem.quantity;

        // Check if inventory limit was hit (Shopify capped it)
        if (actualQuantity < requestedQuantity) {
          await this.handleInventoryLimit(key, actualQuantity, requestedQuantity, cartItem, state);
        } else {
          // Normal success
          this.replaceCartContent(state);
          this.showMessage('success', window.theme.cartToastStrings.quantityUpdated);
        }

      } catch (error) {
        console.error('Error updating cart:', error);
        this.handleUpdateError(error.message, previousValue);
      } finally {
        document.querySelector('.thb-cart-form')?.classList.remove('loading');
      }
    }

    async handleInventoryLimitFromResponse(state, key, requestedQuantity) {
      // Parse the available quantity from Shopify's response message
      // Message format: "Only X items were added to your cart due to availability."
      const message = state.message || state.description || '';
      const match = message.match(/Only (\d+) items?/i);
      const availableQuantity = match ? parseInt(match[1]) : null;

      if (availableQuantity !== null && availableQuantity > 0) {
        // Make a new request to set the cart to the available quantity
        try {
          const correctionResponse = await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              updates: { [key]: availableQuantity },
              sections: this.getSectionsToRender().map(section => section.section),
              sections_url: window.location.pathname
            })
          });

          const correctedState = await correctionResponse.json();

          // Update all cart sections with the corrected state
          this.replaceCartContent(correctedState);

          // Get product details for better messaging
          const cartItem = correctedState.items?.find(item => item.key === key);
          const productTitle = cartItem?.product_title || 'This item';
          const variantTitle = cartItem?.variant_title;
          
          let itemDescription = productTitle;
          if (variantTitle && variantTitle !== 'Default Title') {
            itemDescription += ` (${variantTitle})`;
          }

          // Show clear warning message
          const warningMessage = window.theme.cartToastStrings.quantityLimitAvailableForVariant
            .replace(/__QTY__/g, availableQuantity)
            .replace(/__VARIANT__/g, itemDescription);

          this.showMessage('error', warningMessage);

          // Sync the dropdown to show the correct available quantity
          setTimeout(() => {
            this.syncDropdownWithCart(key, availableQuantity);
          }, 150);

        } catch (correctionError) {
          console.error('Error correcting to available quantity:', correctionError);
          
          // Fallback: try to get fresh cart state
          await this.refreshCart();
          this.showMessage('warning', message);
        }
      } else if (availableQuantity === 0) {
        // Out of stock - refresh cart
        await this.refreshCart();
        this.showMessage('error', window.theme.cartToastStrings.quantityItemOutOfStock);
      } else {
        // Couldn't parse available quantity - refresh cart as fallback
        await this.refreshCart();
        this.showMessage('warning', message || window.theme.cartToastStrings.quantityUpdateFailed);
      }
    }

    async handleInventoryLimit(key, actualQuantity, requestedQuantity, cartItem, currentState) {
      // Update cart sections immediately
      this.replaceCartContent(currentState);

      // Build descriptive message
      const productTitle = cartItem.product_title || 'This item';
      const variantTitle = cartItem.variant_title;
      
      let itemDescription = productTitle;
      if (variantTitle && variantTitle !== 'Default Title') {
        itemDescription += ` (${variantTitle})`;
      }

      const message = window.theme.cartToastStrings.quantityLimitAvailableForVariant
        .replace(/__QTY__/g, actualQuantity)
        .replace(/__VARIANT__/g, itemDescription);

      this.showMessage('err', message);

      // Sync dropdown after DOM updates
      setTimeout(() => {
        this.syncDropdownWithCart(key, actualQuantity);
      }, 150);
    }

    async refreshCart() {
      try {
        const response = await fetch('/cart.js', {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
        
        const cartData = await response.json();
        
        // Trigger a page refresh or update sections
        const sectionsResponse = await fetch(window.location.pathname, {
          headers: {
            'Accept': 'application/json'
          }
        });
        
        if (sectionsResponse.ok) {
          const sectionsData = await sectionsResponse.json();
          this.replaceCartContent({ items: cartData.items, sections: sectionsData });
        } else {
          // Hard refresh as last resort
          window.location.reload();
        }
      } catch (error) {
        console.error('Error refreshing cart:', error);
        window.location.reload();
      }
    }

    syncDropdownWithCart(key, correctQuantity) {
      // Find the refreshed dropdown for this specific item
      const updatedSelector = document.querySelector(`select[data-key="${key}"]`);
      
      if (updatedSelector) {
        // Find and select the option matching the actual quantity
        const options = Array.from(updatedSelector.options);
        const matchingOption = options.find(opt => parseInt(opt.value) === correctQuantity);
        
        if (matchingOption) {
          updatedSelector.value = correctQuantity;
          matchingOption.selected = true;
          
          // Remove any existing 'selected' attributes and add to correct option
          options.forEach(opt => opt.removeAttribute('selected'));
          matchingOption.setAttribute('selected', 'selected');
        } else {
          // If exact match not found, select closest available option
          const validOptions = options.filter(opt => opt.value);
          if (validOptions.length > 0) {
            const closestOption = validOptions.reduce((prev, curr) => {
              return Math.abs(parseInt(curr.value) - correctQuantity) < Math.abs(parseInt(prev.value) - correctQuantity) 
                ? curr 
                : prev;
            });
            
            updatedSelector.value = closestOption.value;
            options.forEach(opt => opt.removeAttribute('selected'));
            closestOption.setAttribute('selected', 'selected');
          }
        }
        
        // Store the current value for future rollback scenarios
        updatedSelector.dataset.originalValue = updatedSelector.value;
      } else {
        console.warn('Could not find dropdown to sync:', key);
      }
    }

    // NEW METHOD: Update dropdown max quantity dynamically
    updateDropdownMaxQuantity(key, maxInventory) {
      const selector = document.querySelector(`select[data-key="${key}"]`);
      
      if (!selector) {
        console.warn('Selector not found for key:', key);
        return;
      }

      const currentValue = parseInt(selector.value) || 1;
      const minQty = 1;
      const step = 1;
      
      // Determine max quantity (minimum of 5 or available inventory)
      const maxQty = Math.min(5, Math.max(1, maxInventory));
      
      console.log('Updating dropdown max quantity:', {
        key,
        currentValue,
        maxInventory,
        calculatedMax: maxQty
      });
      
      // Clear existing options
      selector.innerHTML = '';
      
      // Rebuild options with new max
      for (let i = minQty; i <= maxQty; i += step) {
        const option = document.createElement('option');
        option.value = i;
        option.textContent = i;
        
        // Maintain current selection if still valid
        if (i === currentValue && currentValue <= maxQty) {
          option.selected = true;
          option.setAttribute('selected', 'selected');
        }
        
        selector.appendChild(option);
      }
      
      // If current value exceeds new max, select the max
      if (currentValue > maxQty) {
        selector.value = maxQty;
        const lastOption = selector.querySelector(`option[value="${maxQty}"]`);
        if (lastOption) {
          lastOption.selected = true;
          lastOption.setAttribute('selected', 'selected');
        }
      }
      
      // Store the max for reference
      selector.dataset.maxInventory = maxQty;
      selector.dataset.originalValue = selector.value;
    }

    handleUpdateError(errorMessage, previousValue) {
      // Rollback dropdown to previous value
      if (this.select && previousValue) {
        this.select.value = previousValue;
        const options = Array.from(this.select.options);
        const previousOption = options.find(opt => opt.value === previousValue);
        if (previousOption) {
          previousOption.selected = true;
        }
      }

      const message = typeof errorMessage === 'string'
        ? errorMessage
        : window.theme.cartToastStrings.quantityGenericError;
      
      this.showMessage('error', message);
    }

    showMessage(type, message) {
      const appMessage = document.querySelector('app-message');
      if (appMessage && typeof appMessage.show === 'function') {
        appMessage.show(type, message);
      } else {
        console[type === 'error' ? 'error' : 'log'](`[${type.toUpperCase()}] ${message}`);
        this.showFallbackNotification(type, message);
      }
    }

    showFallbackNotification(type, message) {
      const existingNotification = document.querySelector('.cart-qty-notification');
      if (existingNotification) {
        existingNotification.remove();
      }

      const notification = document.createElement('div');
      notification.className = `cart-qty-notification cart-qty-notification--${type}`;
      notification.textContent = message;
      
      const colors = {
        error: { bg: '#dc2626', text: '#ffffff' },
        warning: { bg: '#f59e0b', text: '#ffffff' },
        success: { bg: '#10b981', text: '#ffffff' }
      };
      
      const colorScheme = colors[type] || colors.success;
      
      Object.assign(notification.style, {
        position: 'fixed',
        top: '20px',
        right: '20px',
        padding: '16px 24px',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        zIndex: '10000',
        maxWidth: '400px',
        fontSize: '14px',
        lineHeight: '1.5',
        fontWeight: '500',
        animation: 'slideInRight 0.3s ease-out',
        backgroundColor: colorScheme.bg,
        color: colorScheme.text
      });

      document.body.appendChild(notification);

      setTimeout(() => {
        notification.style.animation = 'slideOutRight 0.3s ease-out';
        setTimeout(() => notification.remove(), 300);
      }, 5000);
    }

    getSectionsToRender() {
      return [
        { id: 'Cart', section: document.getElementById('Cart')?.dataset.id, selector: '.thb-cart-form' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
      ];
    }

    replaceCartContent(parsedState) {
      this.getSectionsToRender().forEach((section) => {
        const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
        
        if (elementToReplace && parsedState.sections && parsedState.sections[section.section]) {
          const newHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
          
          if (newHTML) {
            elementToReplace.innerHTML = newHTML;
          }
        }
      });

      document.dispatchEvent(new CustomEvent('cart:updated', { 
        detail: { cart: parsedState } 
      }));
    }

    getSectionInnerHTML(html, selector) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const element = doc.querySelector(selector);
      return element ? element.innerHTML : '';
    }

    change_quantity(change) {
      let quantity = Number(this.input.value);
      if (isNaN(quantity)) quantity = 1;

      const min = parseInt(this.input.getAttribute('min')) || 1;
      const max = this.input.getAttribute('max') ? parseInt(this.input.getAttribute('max')) : Infinity;

      if (quantity + change < min || quantity + change > max) {
        return;
      }

      quantity += change;
      quantity = Math.max(quantity, 1);

      this.input.value = quantity;
      this.input.dispatchEvent(this.changeEvent);
      this.validateQtyRules();
    }

    validateQtyRules() {
      const value = parseInt(this.input.value);
      if (this.input.min) {
        const min = parseInt(this.input.min);
        this.subtract.classList.toggle('disabled', value <= min);
      }
      if (this.input.max) {
        const max = parseInt(this.input.max);
        this.add.classList.toggle('disabled', value >= max);
      }
    }
  }
  
  customElements.define('cart-quantity-selector', QuantityInput);

  if (!document.querySelector('#cart-qty-animations')) {
    const style = document.createElement('style');
    style.id = 'cart-qty-animations';
    style.textContent = `
      @keyframes slideInRight {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      @keyframes slideOutRight {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(100%);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

/**
 *  @class
 *  @function ArrowSubMenu
 */
class ArrowSubMenu {

  constructor(self) {
    this.submenu = self.parentNode.querySelector('.sub-menu');
    this.arrow = self;
    // Add functionality to buttons
    self.addEventListener('click', (e) => this.toggle_submenu(e));
  }

  toggle_submenu(e) {
    e.preventDefault();
    let submenu = this.submenu;

    if (!submenu.classList.contains('active')) {
      submenu.classList.add('active');

    } else {
      submenu.classList.remove('active');
      this.arrow.blur();
    }
  }
}
let arrows = document.querySelectorAll('.thb-arrow');
arrows.forEach((arrow) => {
  new ArrowSubMenu(arrow);
});

/**
 *  @class
 *  @function ProductCard
 */
if (!customElements.get('product-card')) {
  class ProductCard extends HTMLElement {
    constructor() {
      super();

      let _this = this;
      this.productCard = this
      this.querySelectorAll(".product-card-swatch").forEach((swatch) => {
        swatch.addEventListener('click', _this.onVariantChange.bind(this));
      })

      this.swatches = this.querySelector('.product-card-swatches');
      this.image = this.querySelector('.product-card--featured-image-link .product-primary-image');
      this.additional_images = this.querySelectorAll('.product-secondary-image');
      this.additional_images_nav = this.querySelectorAll('.product-secondary-images-nav li');
      this.quick_add = this.querySelector('.product-card--add-to-cart-button-simple');
      this.size_options = this.querySelector('.product-card-sizes');
      this.container = this.querySelector(".product-card-swatches");
      this.btnRight = this.querySelector(".color-swatch-view-more");
      this.updateRightButton = this.updateRightButton.bind(this);

      if (this.container && this.btnRight) {
        this.btnRight.addEventListener("click", () => {
          this.container.scrollBy({ left: 50, behavior: "smooth" });
        });
        this.container.addEventListener("scroll", this.updateRightButton);
        window.addEventListener("resize", this.updateRightButton);
        this.updateRightButton();
      }
    }

    onVariantChange(event) {
      event.preventDefault(); 
      this.renderProductCard(event);
    }

    async renderProductCard(event) {
      event.preventDefault();
      this.productSwatch = event.currentTarget;
      this.productUrl = this.productSwatch.getAttribute("data-url");

      let variantUrl = `${this.productUrl}?view=card`;

      try {
        const response = await fetch(variantUrl);
        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, "text/html");

        const newCard = html.querySelector("product-card");

        if (!newCard) {
          console.error("No product-card found in fetched HTML");
          return;
        }
        this.insertAdjacentHTML("afterend", newCard.outerHTML);

        const oldCard = this;
        oldCard.remove();

        const insertedCard = this.nextElementSibling
          ? this.nextElementSibling
          : document.querySelector(`product-card[data-handle="${newCard.dataset.handle}"]`);

        if (insertedCard) {
          customElements.upgrade(insertedCard);
        }

      } catch (error) {
        console.error("Error fetching variant card:", error);
      }
    }

    connectedCallback() {
      if (this.swatches) {
        this.enableSwatches(this.swatches, this.image);
      }
      if (this.additional_images) {
        this.enableAdditionalImages();
      }
      if (this.quick_add) {
        this.enableQuickAdd();
      }
      if (this.size_options) {
        this.enableSizeOptions();
      }
    }

    updateRightButton() {
      if (this.container.scrollWidth <= this.container.clientWidth) {
        this.btnRight.style.display = "none";
        return;
      }
      this.btnRight.style.display = "flex";

      this.btnRight.disabled =
        this.container.scrollLeft + this.container.clientWidth >= this.container.scrollWidth;
    }
    enableAdditionalImages() {
      let image_length = this.additional_images.length;
      let images = this.additional_images;
      let nav = this.additional_images_nav;
      let image_container = this.querySelector('.product-card--featured-image-link');
      const mousemove = function (e) {
        let l = e.offsetX;
        let w = this.getBoundingClientRect().width;
        let prc = l / w;
        let sel = Math.floor(prc * image_length);
        let selimg = images[sel];
        images.forEach((image, index) => {
          if (image.classList.contains('hover')) {
            image.classList.remove('hover');
            if (nav.length) {
              nav[index].classList.remove('active');
            }
          }
        });
        if (selimg) {
          if (!selimg.classList.contains('hover')) {
            selimg.classList.add('hover');
            if (nav.length) {
              nav[sel].classList.add('active');
            }
          }
        }

      };
      const mouseleave = function (e) {
        images.forEach((image, index) => {
          image.classList.remove('hover');
          if (nav.length) {
            nav[index].classList.remove('active');
          }
        });
      };
      if (image_container) {
        image_container.addEventListener('touchstart', mousemove, {
          passive: true
        });
        image_container.addEventListener('touchmove', mousemove, {
          passive: true
        });
        image_container.addEventListener('touchend', mouseleave, {
          passive: true
        });
        image_container.addEventListener('mouseenter', mousemove, {
          passive: true
        });
        image_container.addEventListener('mousemove', mousemove, {
          passive: true
        });
        image_container.addEventListener('mouseleave', mouseleave, {
          passive: true
        });
      }

      images.forEach(function (image) {
        window.addEventListener('load', (event) => {
          lazySizes.loader.unveil(image);
        });
      });

    }
    enableSwatches(swatches, image) {
      let swatch_list = swatches.querySelectorAll('.product-card-swatch'),
        org_srcset = image ? image.dataset.srcset : '';
      this.color_index = this.swatches.dataset.index;

      swatch_list.forEach((swatch, index) => {
        window.addEventListener('load', (event) => {
          let image = new Image();
          image.srcset = swatch.dataset.srcset;
          lazySizes.loader.unveil(image);
        });

        swatch.addEventListener('click', () => {
          [].forEach.call(swatch_list, function (el) {
            el.classList.remove('active');
          });
          if (image) {
            if (swatch.dataset.srcset) {
              image.setAttribute('srcset', swatch.dataset.srcset);
            } else {
              image.setAttribute('srcset', org_srcset);
            }
          }
          if (this.size_options) {
            this.current_options[this.color_index] = swatch.querySelector('span').innerText;
            this.updateMasterId();
          }
          swatch.classList.add('active');
        });
        // swatch.addEventListener('click', function (evt) {
        //   window.location.href = this.dataset.href;
        //   evt.preventDefault();
        // });
      });
    }
    enableQuickAdd() {
      this.quick_add.addEventListener('click', this.quickAdd.bind(this));
    }
    enableSizeOptions() {
      let size_list = this.size_options.querySelectorAll('.product-card-sizes--size'),
        featured_image = this.querySelector('.product-card--featured-image'),
        has_hover = featured_image.classList.contains('thb-hover'),
        size_parent = this.size_options.parentElement;

      this.size_index = this.size_options.dataset.index;

      this.current_options = this.size_options.dataset.options.split(',');

      this.updateMasterId();

      size_parent.addEventListener('mouseenter', () => {
        if (has_hover) {
          featured_image.classList.remove('thb-hover');
        }
      }, {
        passive: true
      });
      size_parent.addEventListener('mouseleave', () => {
        if (has_hover) {
          featured_image.classList.add('thb-hover');
        }
      }, {
        passive: true
      });
      size_list.forEach((size) => {
        size.addEventListener('click', (evt) => {
          evt.preventDefault();

          if (size.classList.contains('is-disabled')) {
            return;
          }
          this.current_options[this.size_index] = size.querySelector('span').innerText;
          this.updateMasterId();

          size.classList.add('loading');
          size.setAttribute('aria-disabled', true);
          const config = {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'Accept': 'application/javascript'
            }
          };
          let formData = new FormData();

          formData.append('id', this.currentVariant.id);
          formData.append('quantity', 1);
          formData.append('sections', this.getSectionsToRender().map((section) => section.section));
          formData.append('sections_url', window.location.pathname);

          config.body = formData;

          fetch(`${theme.routes.cart_add_url}`, config)
            .then((response) => response.json())
            .then((response) => {
              if (response.status) {
                return;
              }
              this.renderContents(response);

              dispatchCustomEvent('cart:item-added', {
                product: response.hasOwnProperty('items') ? response.items[0] : response
              });
            })
            .catch((e) => {
              console.error(e);
            })
            .finally(() => {
              size.classList.remove('loading');
              size.removeAttribute('aria-disabled');
            });
        });
      });
    }
    updateMasterId() {
      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options.map((option, index) => {
          return this.current_options[index] === option;
        }).includes(false);
      });
      setTimeout(() => {
        this.setDisabled();
      }, 100);
    }
    getVariantData() {
      this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
      return this.variantData;
    }
    setDisabled() {
      const variant_data = this.getVariantData();

      if (variant_data) {

        if (this.currentVariant) {
          const selected_options = this.currentVariant.options.map((value, index) => {
            return {
              value,
              index: `option${index + 1}`
            };
          });

          const available_options = this.createAvailableOptionsTree(variant_data, selected_options);

          const fieldset_options = Object.values(available_options)[this.size_index];
          if (fieldset_options) {
            if (this.size_options.querySelectorAll('.product-card-sizes--size').length) {
              this.size_options.querySelectorAll('.product-card-sizes--size').forEach((input, input_i) => {
                input.classList.toggle('is-disabled', fieldset_options[input_i].isUnavailable);
              });
            }
          }
        } else {
          if (this.size_options.querySelectorAll('.product-card-sizes--size').length) {
            this.size_options.querySelectorAll('.product-card-sizes--size').forEach((input, input_i) => {
              input.classList.add('is-disabled');
            });
          }
        }

      }
      return true;
    }
    createAvailableOptionsTree(variant_data, selected_options) {
      // Reduce variant array into option availability tree
      return variant_data.reduce((options, variant) => {

        // Check each option group (e.g. option1, option2, option3) of the variant
        Object.keys(options).forEach(index => {

          if (variant[index] === null) return;

          let entry = options[index].find(option => option.value === variant[index]);

          if (typeof entry === 'undefined') {
            // If option has yet to be added to the options tree, add it
            entry = {
              value: variant[index],
              isUnavailable: true
            };
            options[index].push(entry);
          }

          // Check how many selected option values match a variant
          const countVariantOptionsThatMatchCurrent = selected_options.reduce((count, {
            value,
            index
          }) => {
            return variant[index] === value ? count + 1 : count;
          }, 0);

          // Only enable an option if an available variant matches all but one current selected value
          if (countVariantOptionsThatMatchCurrent >= selected_options.length - 1) {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }

          // Make sure if a variant is unavailable, disable currently selected option
          if ((!this.currentVariant || !this.currentVariant.available) && selected_options.find((option) => option.value === entry.value && index === option.index)) {
            entry.isUnavailable = true;
          }

          // First option is always enabled
          if (index === 'option1') {
            entry.isUnavailable = entry.isUnavailable && variant.available ? false : entry.isUnavailable;
          }
        });

        return options;
      }, {
        option1: [],
        option2: [],
        option3: []
      });
    }
    quickAdd(evt) {
      evt.preventDefault();
      if (this.quick_add.disabled) {
        return;
      }
      this.quick_add.classList.add('loading');
      this.quick_add.setAttribute('aria-disabled', true);

      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        }
      };

      let formData = new FormData();

      formData.append('id', this.quick_add.dataset.productId);
      formData.append('quantity', 1);
      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);

      config.body = formData;

      fetch(`${theme.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            return;
          }
          this.renderContents(response);

          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response
          });
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.quick_add.classList.remove('loading');
          this.quick_add.removeAttribute('aria-disabled');
        });

      return false;
    }
    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: document.getElementById('Cart')?.dataset.id,
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    renderContents(parsedState) {
      this.getSectionsToRender().forEach((section => {
        if (!document.getElementById(section.id)) {
          return;
        }
        const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

        if (section.id === 'Cart-Drawer') {
          document.getElementById('Cart-Drawer')?.notesToggle();
          document.getElementById('Cart-Drawer')?.removeProductEvent();
        }

        if (section.id === 'Cart' && typeof Cart !== 'undefined') {
          new Cart().renderContents(parsedState);
        }
      }));


      if (document.getElementById('Cart-Drawer')) {
        document.getElementById('Cart-Drawer').classList.add('active');
        document.body.classList.add('open-cart');
        document.body.classList.add('open-cc');
        if (document.getElementById('Cart-Drawer').querySelector('.product-recommendations--full')) {
          document.getElementById('Cart-Drawer').querySelector('.product-recommendations--full').classList.add('active');
        }
        dispatchCustomEvent('cart-drawer:open');
      }
    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
  }
  customElements.define('product-card', ProductCard);
}


/**
 *  @class
 *  @function PanelClose
 */
if (!customElements.get('side-panel-close')) {
  class PanelClose extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.cc = document.querySelector('.click-capture');

      this.onClick = (e) => {
        let panel = document.querySelectorAll('.side-panel.active');
        document.querySelector("body").classList.remove("overflow-hidden");
        if (panel.length) {
          this.close_panel(e, panel[0]);
        }
      };
      // Add functionality to buttons
      this.addEventListener('click', this.onClick.bind(this));
      document.addEventListener('panel:close', this.onClick.bind(this));
      if (!this.cc.hasAttribute('initialized')) {
        this.cc.addEventListener('click', this.onClick.bind(this));
        this.cc.setAttribute('initialized', '');
      }
    }
    close_panel(e, panel) {
      if (e) {
        e.preventDefault();
      }
      if (!panel) {
        panel = e?.target.closest('.side-panel.active');

        if (!panel) {
          return;
        }
      }
      if (panel.classList.contains('product-drawer') || document.body.classList.contains('open-quick-view')) {
        this.close_quick_view();
      } else if (panel.classList.contains('cart-drawer')) {
        if (panel.querySelector('.product-recommendations--full')) {
          if (!document.body.classList.contains('open-quick-view')) {
            panel.querySelector('.product-recommendations--full').classList.remove('active');
          }
        }
        if (window.innerWidth < 1069) {
          if (!document.body.classList.contains('open-quick-view')) {
            panel.classList.remove('active');
            document.body.classList.remove('open-cc');
            document.body.classList.remove('open-cart');
          } else {
            this.close_quick_view();
          }
        } else {
          if (panel.querySelector('.product-recommendations--full')) {
            if (!document.body.classList.contains('open-quick-view')) {
              setTimeout(() => {
                panel.classList.remove('active');
                document.body.classList.remove('open-cc');
                document.body.classList.remove('open-cart');
              }, 500);
            } else {
              this.close_quick_view();
            }
          } else {
            panel.classList.remove('active');
            document.body.classList.remove('open-cc');
            document.body.classList.remove('open-cart');
          }
        }
      } else {
        panel.classList.remove('active');
        document.body.classList.remove('open-cc');
      }
    }
    close_quick_view() {
      let panel = document.getElementById('Product-Drawer');

      if (panel.querySelector('.product-quick-images--container')) {
        panel.querySelector('.product-quick-images--container').classList.remove('active');
      }
      if (window.innerWidth < 1069) {
        panel.classList.remove('active');
        if (!document.body.classList.contains('open-cart') || !document.body.classList.contains('open-quick-view')) {
          document.body.classList.remove('open-cc');
        }
        document.body.classList.remove('open-quick-view');
      } else {
        if (panel.querySelector('.product-quick-images--container')) {
          setTimeout(() => {
            panel.classList.remove('active');
            if (!document.body.classList.contains('open-cart') || !document.body.classList.contains('open-quick-view')) {
              document.body.classList.remove('open-cc');
            }
            document.body.classList.remove('open-quick-view');
            panel.querySelector('#Product-Drawer-Content').innerHTML = '';
          }, 500);
        }
      }
    }
  }
  customElements.define('side-panel-close', PanelClose);

  document.addEventListener('keyup', (e) => {
    if (e.code) {
      if (e.code.toUpperCase() === 'ESCAPE') {
        dispatchCustomEvent('panel:close');
      }
    }
  });
}
/**
 *  @class
 *  @function CartDrawer
 */
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {

    constructor() {
      super();
    }

    connectedCallback() {
      let button = document.getElementById('cart-drawer-toggle');

      // Add functionality to buttons
      button.addEventListener('click', (e) => {
        e.preventDefault();
        document.body.classList.add('open-cc');
        document.body.classList.add('open-cart');
        this.classList.add('active');
        this.focus();
        setTimeout(() => {
          this.querySelector('.product-recommendations--full')?.classList.add('active');
        });
        dispatchCustomEvent('cart-drawer:open');
      });

      this.debouncedOnChange = debounce((event) => {
        this.onChange(event);
      }, 300);

      document.addEventListener('cart:refresh', (event) => {
        this.refresh();
      });

      this.addEventListener('change', this.debouncedOnChange.bind(this));

      this.notesToggle();
      this.removeProductEvent();
    }
    onChange(event) {
      if (event.target.classList.contains('qty')) {
        this.updateQuantity(event.target.dataset.index, event.target.value);
      }
    }
    removeProductEvent() {
      let removes = this.querySelectorAll('.remove');

      removes.forEach((remove) => {
        remove.addEventListener('click', (event) => {
          this.updateQuantity(event.target.dataset.index, '0');

          event.preventDefault();
        });
      });
    }
    getSectionsToRender() {
      return [{
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-item-count'
      }];
    }
    getSectionInnerHTML(html, selector) {
      return new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(selector).innerHTML;
    }
    notesToggle() {
      const notes_toggle = document.getElementById('order-note-toggle');

      if (!notes_toggle) {
        return;
      }

      notes_toggle.addEventListener('click', (event) => {
        notes_toggle.nextElementSibling.classList.add('active');
      });
      notes_toggle.nextElementSibling.querySelectorAll('.button, .order-note-toggle__content-overlay').forEach((el) => {
        el.addEventListener('click', (event) => {
          notes_toggle.nextElementSibling.classList.remove('active');
          this.saveNotes();
        });
      });
    }
    saveNotes() {
      fetch(`${theme.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': `application/json`
        },
        body: JSON.stringify({
          'note': document.getElementById('mini-cart__notes').value
        })
      });
    }
    updateQuantity(line, quantity) {
      this.querySelector(`#CartDrawerItem-${line}`)?.classList.add('thb-loading');
      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      });

      dispatchCustomEvent('line-item:change:start', {
        quantity: quantity
      });
      this.querySelector('.product-recommendations--full')?.classList.remove('active');

      fetch(`${theme.routes.cart_change_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': `application/json`
        },
        ...{
          body
        }
      })
        .then((response) => {
          return response.text();
        })
        .then((state) => {
          const parsedState = JSON.parse(state);

          this.getSectionsToRender().forEach((section => {
            const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

            if (parsedState.sections) {
              elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
            }
          }));

          this.removeProductEvent();
          this.notesToggle();
          dispatchCustomEvent('line-item:change:end', {
            quantity: quantity,
            cart: parsedState
          });

          this.querySelector(`#CartDrawerItem-${line}`)?.classList.remove('thb-loading');
        });
    }
    refresh() {
      this.querySelector('.product-recommendations--full')?.classList.remove('active');
      let sections = 'cart-drawer,cart-bubble';
      fetch(`${window.location.pathname}?sections=${sections}`)
        .then((response) => {
          return response.text();
        })
        .then((state) => {
          const parsedState = JSON.parse(state);

          this.getSectionsToRender().forEach((section => {
            const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

            elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState[section.section], section.selector);
          }));

          this.removeProductEvent();
          this.notesToggle();
        });
    }
  }
  customElements.define('cart-drawer', CartDrawer);
}

/**
 *  @class
 *  @function SelectWidth
 */
class SelectWidth {
  constructor() {
    let _this = this;
    // resize on initial load
    window.addEventListener('load', () => {
      document.querySelectorAll('.resize-select').forEach(_this.resizeSelect);
    });

    // delegated listener on change
    document.body.addEventListener('change', (e) => {
      if (e.target.matches('.resize-select') && e.target.offsetParent !== null) {
        _this.resizeSelect(e.target);
      }
    });
    window.addEventListener('resize.resize-select', function () {
      document.querySelectorAll('.resize-select').forEach(_this.resizeSelect);
    });
  }

  resizeSelect(sel) {
    let tempOption = document.createElement('option');
    tempOption.textContent = sel.selectedOptions[0].textContent;

    let tempSelect = document.createElement('select'),
      offset = 13;
    tempSelect.style.visibility = 'hidden';
    tempSelect.style.position = 'fixed';
    tempSelect.appendChild(tempOption);
    if (sel.classList.contains('thb-language-code') || sel.classList.contains('thb-currency-code') || sel.classList.contains('facet-filters__sort')) {
      offset = 2;
    }
    sel.after(tempSelect);
    if (tempSelect.clientWidth > 0) {
      sel.style.width = `${+tempSelect.clientWidth + offset}px`;
    }
    tempSelect.remove();
  }
}

// if (typeof SelectWidth !== 'undefined') {
//   new SelectWidth();
// }

/**
 *  @class
 *  @function FooterMenuToggle
 */
class FooterMenuToggle {
  constructor() {
    let _this = this;
    // resize on initial load
    document.querySelectorAll('.thb-widget-title.collapsible').forEach((button) => {
      button.addEventListener('click', (e) => {
        button.classList.toggle('active');
      });
    });
  }
}

/**
 *  @class
 *  @function QuickView
 */
if (!customElements.get('quick-view')) {
  class QuickView extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.drawer = document.getElementById('Product-Drawer');
      this.body = document.body;

      this.addEventListener('click', this.setupEventListener.bind(this));

    }
    setupEventListener(e) {
      e.preventDefault();
      let productHandle = this.dataset.productHandle,
        href = `${theme.routes.root_url}/products/${productHandle}?view=quick-view`;

      // remove double `/` in case shop might have /en or language in URL
      href = href.replace('//', '/');
      if (!href || !productHandle) {
        return;
      }
      if (this.classList.contains('loading')) {
        return;
      }
      this.classList.add('loading');
      fetch(href, {
        method: 'GET'
      })
        .then((response) => {
          this.classList.remove('loading');
          return response.text();
        })
        .then(text => {
          const sectionInnerHTML = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#Product-Drawer-Content').innerHTML;

          this.renderQuickview(sectionInnerHTML, href, productHandle);

        });
    }
    renderQuickview(sectionInnerHTML, href, productHandle) {
      if (sectionInnerHTML) {

        this.drawer.querySelector('#Product-Drawer-Content').innerHTML = sectionInnerHTML;

        let js_files = this.drawer.querySelector('#Product-Drawer-Content').querySelectorAll('script');

        if (js_files.length > 0) {
          var head = document.getElementsByTagName('head')[0];
          js_files.forEach((js_file, i) => {
            let script = document.createElement('script');
            script.src = js_file.src;
            head.appendChild(script);
          });
        }

        setTimeout(() => {
          if (Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }
          if (window.ProductModel) {
            window.ProductModel.loadShopifyXR();
          }
        }, 300);

        this.body.classList.add('open-cc');
        this.body.classList.add('open-quick-view');
        this.drawer.classList.add('active');

        this.drawer.querySelector('.side-panel-close').focus();

        setTimeout(() => {
          this.drawer.querySelector('.product-quick-images--container').classList.add('active');
        });
        dispatchCustomEvent('quick-view:open', {
          productUrl: href,
          productHandle: productHandle
        });
        addIdToRecentlyViewed(productHandle);
      }
    }
  }
  customElements.define('quick-view', QuickView);
}

/**
 *  @class
 *  @function SidePanelContentTabs
 */
if (!customElements.get('side-panel-content-tabs')) {
  class SidePanelContentTabs extends HTMLElement {
    constructor() {
      super();
      this.buttons = this.querySelectorAll('button');
      this.panels = this.parentElement.querySelectorAll('.side-panel-content--tab-panel');
    }
    connectedCallback() {
      this.setupButtonObservers();
    }
    disconnectedCallback() {

    }
    setupButtonObservers() {
      this.buttons.forEach((item, i) => {
        item.addEventListener('click', (e) => {
          this.toggleActiveClass(i);
        });
      });
    }
    toggleActiveClass(i) {
      this.buttons.forEach((button) => {
        button.classList.remove('tab-active');
      });
      this.buttons[i].classList.add('tab-active');

      this.panels.forEach((panel) => {
        panel.classList.remove('tab-active');
      });
      this.panels[i].classList.add('tab-active');
    }
  }

  customElements.define('side-panel-content-tabs', SidePanelContentTabs);
}

/**
 *  @class
 *  @function CollapsibleRow
 */
if (!customElements.get('collapsible-row')) {
  // https://css-tricks.com/how-to-animate-the-details-element/
  class CollapsibleRow extends HTMLElement {
    constructor() {
      super();

      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.content = this.querySelector('.collapsible__content');

      // Store the animation object (so we can cancel it if needed)
      this.animation = null;
      // Store if the element is closing
      this.isClosing = false;
      // Store if the element is expanding
      this.isExpanding = false;
    }
    connectedCallback() {
      this.setListeners();
    }
    setListeners() {
      this.querySelector('summary').addEventListener('click', (e) => this.onClick(e));
    }
    instantClose() {
      this.tl.timeScale(10).reverse();
    }
    animateClose() {
      this.tl.timeScale(3).reverse();
    }
    animateOpen() {
      this.tl.timeScale(1).play();
    }
    onClick(e) {
      // Stop default behaviour from the browser
      e.preventDefault();
      // Add an overflow on the <details> to avoid content overflowing
      this.details.style.overflow = 'hidden';
      // Check if the element is being closed or is already closed
      if (this.isClosing || !this.details.open) {
        this.open();
        // Check if the element is being openned or is already open
      } else if (this.isExpanding || this.details.open) {
        this.shrink();
      }
    }
    shrink() {
      // Set the element as "being closed"
      this.isClosing = true;

      // Store the current height of the element
      const startHeight = `${this.details.offsetHeight}px`;
      // Calculate the height of the summary
      const endHeight = `${this.summary.offsetHeight}px`;

      // If there is already an animation running
      if (this.animation) {
        // Cancel the current animation
        this.animation.cancel();
      }

      // Start a WAAPI animation
      this.animation = this.details.animate({
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, endHeight]
      }, {
        duration: 250,
        easing: 'ease'
      });

      // When the animation is complete, call onAnimationFinish()
      this.animation.onfinish = () => this.onAnimationFinish(false);
      // If the animation is cancelled, isClosing variable is set to false
      this.animation.oncancel = () => this.isClosing = false;
    }

    open() {
      // Apply a fixed height on the element
      this.details.style.height = `${this.details.offsetHeight}px`;
      // Force the [open] attribute on the details element
      this.details.open = true;
      // Wait for the next frame to call the expand function
      window.requestAnimationFrame(() => this.expand());
    }

    expand() {
      // Set the element as "being expanding"
      this.isExpanding = true;
      // Get the current fixed height of the element
      const startHeight = `${this.details.offsetHeight}px`;
      // Calculate the open height of the element (summary height + content height)
      const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

      // If there is already an animation running
      if (this.animation) {
        // Cancel the current animation
        this.animation.cancel();
      }

      // Start a WAAPI animation
      this.animation = this.details.animate({
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, endHeight]
      }, {
        duration: 400,
        easing: 'ease-out'
      });
      // When the animation is complete, call onAnimationFinish()
      this.animation.onfinish = () => this.onAnimationFinish(true);
      // If the animation is cancelled, isExpanding variable is set to false
      this.animation.oncancel = () => this.isExpanding = false;
    }

    onAnimationFinish(open) {
      // Set the open attribute based on the parameter
      this.details.open = open;
      // Clear the stored animation
      this.animation = null;
      // Reset isClosing & isExpanding
      this.isClosing = false;
      this.isExpanding = false;
      // Remove the overflow hidden and the fixed height
      this.details.style.height = this.details.style.overflow = '';
    }
  }
  customElements.define('collapsible-row', CollapsibleRow);
}

/**
 *  @function addIdToRecentlyViewed
 */
function addIdToRecentlyViewed(handle) {

  if (!handle) {
    let product = document.querySelector('.thb-product-detail');

    if (product) {
      handle = product.dataset.handle;
    }
  }
  if (!handle) {
    return;
  }
  if (window.localStorage) {
    let recentIds = window.localStorage.getItem('recently-viewed');
    if (recentIds != 'undefined' && recentIds != null) {
      window.recentlyViewedIds = JSON.parse(recentIds);
    }
  }
  // Remove current product if already in recently viewed array
  var i = window.recentlyViewedIds.indexOf(handle);

  if (i > -1) {
    window.recentlyViewedIds.splice(i, 1);
  }

  // Add id to array
  window.recentlyViewedIds.unshift(handle);

  if (window.localStorage) {
    window.localStorage.setItem('recently-viewed', JSON.stringify(window.recentlyViewedIds));
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (typeof FooterMenuToggle !== 'undefined') {
    new FooterMenuToggle();
  }
});

class ShoeSizeTabs extends HTMLElement {
  constructor() {
    super();
    this.initialized = false;
  }

  connectedCallback() {
    // For Cart modal, slight delay; for PDP, immediate
    const delay = this.dataset.cartMode === "true" ? 50 : 0;
    
    if (!this.initialized) {
      setTimeout(() => this.init(), delay);
    }
  }

  init() {
    this.tabs = this.querySelectorAll(".size-tab");
    
    // Find labels - either inside (cart) or outside (PDP) the custom element
    let labelElements = this.querySelectorAll("label[data-us]");
    let spanElements = this.querySelectorAll("button[data-us-size] span[data-us]");
    
    // If no labels found inside, check parent for PDP structure
    if (labelElements.length === 0 && !this.dataset.cartMode) {
      // PDP: labels are siblings after this element
      const parent = this.parentElement;
      if (parent) {
        labelElements = parent.querySelectorAll("label[data-us]");
      }
    }
    
    // Combine both into a single array
    this.labels = [...labelElements, ...spanElements];
    
    if (this.labels.length === 0) {
      console.warn("⚠️ ShoeSizeTabs: No labels found");
      return;
    }
    
    this.isCartMode = this.dataset.cartMode === "true";

    this.mappings = {
      uk: this.parseMapping(this.dataset.uk),
      eu: this.parseMapping(this.dataset.eu)
    };

    // Store original US values for all labels
    this.originalValues = new Map();
    this.labels.forEach(label => {
      let usValue;
      
      if (label.tagName === "LABEL") {
        usValue = label.dataset.us;
      } else if (label.tagName === "SPAN") {
        usValue = label.dataset.us || label.closest('[data-us-size]')?.dataset.usSize;
      }
      
      if (usValue) {
        this.originalValues.set(label, usValue);
      }
    });

    this.bindEvents();
    this.initialized = true;
  }

  parseMapping(raw) {
    if (!raw) return {};
    return raw
      .split("\n")
      .map(line => line.split(":").map(s => s.trim()))
      .filter(parts => parts.length === 2 && parts[0] && parts[1])
      .reduce((map, [us, other]) => {
        map[us] = other;
        return map;
      }, {});
  }

  bindEvents() {
    if (!this.tabs.length || !this.labels.length) return;

    this.tabs.forEach(tab => {
      tab.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.switchSystem(tab);
      });
    });
  }

  switchSystem(tab) {
    if (!tab) return;

    // Update active tab
    this.tabs.forEach(t => t.classList.remove("active"));
    tab.classList.add("active");
    
    const system = tab.dataset.system;

    // Update all size labels
    this.labels.forEach(label => {
      const usValue = this.originalValues.get(label);
      if (!usValue) return;

      let displayValue = usValue;
      
      if (system !== "us") {
        const mappedValue = this.mappings[system]?.[usValue];
        if (mappedValue) {
          displayValue = mappedValue;
        }
      }

      // Update based on element type
      if (label.tagName === "SPAN") {
        label.textContent = displayValue;
      } else if (label.tagName === "LABEL") {
        label.innerHTML = `<span>${displayValue}</span>`;
      }
    });

    // Cart-specific updates
    if (this.isCartMode) {
      this.dataset.currentSystem = system;
      
      const selectedBtn = this.querySelector('.cart-size-modal__size-btn.is-selected');
      if (selectedBtn) {
        this.updateCartDisplayValue(selectedBtn, system);
      }
    }
  }

  updateCartDisplayValue(button, system) {
    const usValue = button.dataset.usSize;
    const span = button.querySelector('span[data-us]');
    
    if (span && usValue) {
      const displayValue = system === "us" ? usValue : (this.mappings[system]?.[usValue] || usValue);
      const systemLabel = system.toUpperCase();
      
      const modal = this.closest('cart-size-modal');
      if (modal) {
        const trigger = modal.previousElementSibling;
        if (trigger?.classList.contains('cart-size-updater__trigger')) {
          const displaySpan = trigger.querySelector('.cart-shoe-size-display');
          if (displaySpan) {
            displaySpan.textContent = `${displayValue} ${systemLabel}`;
          }
        }
      }
    }
  }

  getCurrentSelection() {
    const activeTab = this.querySelector('.size-tab.active');
    const system = activeTab?.dataset.system || 'us';
    return {
      system: system,
      systemLabel: system.toUpperCase()
    };
  }
}

customElements.define("shoe-size-tabs", ShoeSizeTabs);


class CustomMediaPopupOpener extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.modalId = this.getAttribute('data-modal');

    if (this.button && this.modalId) {
      this.button.addEventListener('click', this.openModal.bind(this));
    }
  }

  openModal(e) {
    e.preventDefault();
    const modal = document.querySelector(this.modalId);
    if (modal) {
      // Get the active slide index FIRST, before adding any classes
      const activeSlideIndex = this.getActiveSlideIndex();
      
      // Check if modal is already initialized
      const mainCarousel = modal.querySelector('.custom-main-carousel');
      const isAlreadyInitialized = mainCarousel && mainCarousel.classList.contains('is-initialized');
      
      if (isAlreadyInitialized) {
        // Hide modal content first to prevent flash of wrong slide
        modal.style.opacity = '0';
      }
      
      modal.classList.add('active');
      document.body.style.overflow = 'hidden';

      setTimeout(() => {
        this.initializeSplide(modal, activeSlideIndex);
        
        // Show modal after slide is set
        if (isAlreadyInitialized) {
          setTimeout(() => {
            modal.style.opacity = '1';
          }, 50);
        }
      }, isAlreadyInitialized ? 0 : 100);

      this.setupCloseHandlers(modal);
    }
  }

  getActiveSlideIndex() {
    // Try to find the active slide in the product slider
    const productSlider = this.closest('.thb-product-detail')?.querySelector('product-slider-thumbnails, product-slider');
    
    if (productSlider) {
      // Check if it's using Flickity
      if (productSlider.flkty) {
        return productSlider.flkty.selectedIndex || 0;
      }
      
      // Fallback: Find the selected slide manually
      const selectedSlide = productSlider.querySelector('.product-images__slide.is-selected');
      if (selectedSlide) {
        const slides = Array.from(productSlider.querySelectorAll('.product-images__slide.is-active'));
        return slides.indexOf(selectedSlide);
      }
    }
    
    // Final fallback: return 0
    return 0;
  }

  setupCloseHandlers(modal) {
    const closeButton = modal.querySelector('[id^="CustomModalClose"]');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.closeModal(modal));
    }

    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        this.closeModal(modal);
      }
    });

    const escHandler = (e) => {
      if (e.key === 'Escape') {
        this.closeModal(modal);
        document.removeEventListener('keydown', escHandler);
      }
    };
    document.addEventListener('keydown', escHandler);
  }

  closeModal(modal) {
    modal.classList.remove('active');
    modal.style.opacity = ''; // Reset opacity
    document.body.style.overflow = '';
  }

  initializeSplide(modal, initialIndex = 0) {
    const mainCarousel = modal.querySelector('.custom-main-carousel');
    const thumbnailCarousel = modal.querySelector('.custom-thumbnails-carousel');

    // Check if already initialized
    if (mainCarousel.classList.contains('is-initialized')) {
      // Try to get the Splide instance from multiple possible locations
      let splideInstance = mainCarousel.splide;
      
      // If not found, try to get it from the Splide Components registry
      if (!splideInstance && window.Splide) {
        const allSplides = document.querySelectorAll('.splide');
        allSplides.forEach(splide => {
          if (splide === mainCarousel && splide.splide) {
            splideInstance = splide.splide;
          }
        });
      }
      
      if (splideInstance) {
        // Go to the new index immediately without animation for instant change
        splideInstance.go(initialIndex, false);
      } else {
        // If we still can't find the instance, destroy and recreate
        console.log('Splide instance not found, reinitializing...');
        mainCarousel.classList.remove('is-initialized');
        mainCarousel.classList.remove('splide--slide');
        mainCarousel.classList.remove('splide--ltr');
        if (thumbnailCarousel) {
          thumbnailCarousel.classList.remove('is-initialized');
          thumbnailCarousel.classList.remove('splide--slide');
          thumbnailCarousel.classList.remove('splide--ltr');
        }
        // Recursively call init again
        this.initializeSplide(modal, initialIndex);
      }
      return;
    }

    const main = new Splide(mainCarousel, {
      type: 'fade',
      rewind: true,
      pagination: false,
      arrows: true,
      heightRatio: 0.6,
      speed: 600,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
      start: initialIndex, // Set the initial slide index
    });

    const thumbnails = new Splide(thumbnailCarousel, {
      fixedWidth: 64,
      fixedHeight: 68,
      gap: 8,
      rewind: true,
      pagination: false,
      isNavigation: true,
      arrows: false,
      focus: 'left',
      padding: { left: 8 },
      start: initialIndex, // Set the initial slide index for thumbnails too
      dragMinThreshold: {
        mouse: 4,
        touch: 10,
      },
      breakpoints: {
        768: {
          fixedWidth: 64,
          fixedHeight: 68,
          gap: 8,
        },
        480: {
          fixedWidth: 64,
          fixedHeight: 68,
          gap: 8,
        },
      },
    });

    main.sync(thumbnails);
    main.mount();
    thumbnails.mount();
    
    // Store the instance on the element for future access
    mainCarousel.splide = main;
    thumbnailCarousel.splide = thumbnails;

    this.initializeInlineZoom(modal, main);
  }

  initializeInlineZoom(modal, splideInstance) {
    const slides = modal.querySelectorAll('.splide__slide');

    slides.forEach((slide) => {
      const link = slide.querySelector('.product-single__media-zoom');
      const img = slide.querySelector('img');

      if (!link || !img) return;

      let fullSizeImageUrl = link.getAttribute('href');

      // Get maximum resolution image (remove Shopify size parameters)
      if (fullSizeImageUrl) {
        fullSizeImageUrl = fullSizeImageUrl.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)\.(jpg|jpeg|png|gif|webp)/gi, '_master.$2');

        if (!fullSizeImageUrl.includes('_master')) {
          const urlParts = fullSizeImageUrl.split('?');
          const baseUrl = urlParts[0];

          if (baseUrl.includes('shopify.com') || baseUrl.includes('cdn.shopify')) {
            const cleanUrl = baseUrl.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048)(?=\.(jpg|jpeg|png|gif|webp))/gi, '');
            fullSizeImageUrl = cleanUrl;
          }
        }
      }

      const zoomContainer = document.createElement('div');
      zoomContainer.className = 'inline-zoom-container';

      const parent = link.parentNode;
      parent.insertBefore(zoomContainer, link);
      zoomContainer.appendChild(link);

      let isZoomed = false;
      let scale = 1;
      let translateX = 0;
      let translateY = 0;
      let isDragging = false;
      let startX = 0;
      let startY = 0;
      let highResLoaded = false;

      const updateTransform = () => {
        link.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
        link.style.transformOrigin = 'center center';
        link.style.transition = isDragging ? 'none' : 'transform 0.3s ease-out';
      };

      const loadHighResImage = () => {
        if (highResLoaded || !fullSizeImageUrl) return;

        const currentImg = link.querySelector('img');
        if (!currentImg) return;

        console.log('Loading high-res image:', fullSizeImageUrl);

        const highResImg = new Image();

        highResImg.onload = () => {
          currentImg.src = fullSizeImageUrl;
          currentImg.srcset = '';
          currentImg.style.imageRendering = 'high-quality';
          currentImg.style.imageRendering = '-webkit-optimize-contrast';

          highResLoaded = true;
          console.log('High-res image loaded successfully');
        };

        highResImg.onerror = () => {
          console.error('Failed to load high-res image:', fullSizeImageUrl);
          const fallbackUrl = link.getAttribute('href');
          if (fallbackUrl && fallbackUrl !== fullSizeImageUrl) {
            highResImg.src = fallbackUrl;
          }
        };

        highResImg.src = fullSizeImageUrl;
      };

      const resetZoom = () => {
        isZoomed = false;
        scale = 1;
        translateX = 0;
        translateY = 0;
        zoomContainer.style.cursor = 'zoom-in';
        link.style.cursor = 'zoom-in';
        updateTransform();
      };

      const zoomIn = (clickX, clickY) => {
        loadHighResImage();

        isZoomed = true;
        scale = 2.5;
        zoomContainer.style.cursor = 'zoom-out';
        link.style.cursor = 'zoom-out';

        const rect = link.getBoundingClientRect();
        const x = clickX - rect.left;
        const y = clickY - rect.top;
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        translateX = (centerX - x) * (scale - 1);
        translateY = (centerY - y) * (scale - 1);

        updateTransform();
      };

      link.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (isZoomed) {
          resetZoom();
        } else {
          zoomIn(e.clientX, e.clientY);
        }
      });

      let lastTap = 0;
      link.addEventListener('touchend', (e) => {
        const currentTime = new Date().getTime();
        const tapLength = currentTime - lastTap;

        if (tapLength < 300 && tapLength > 0) {
          e.preventDefault();
          const touch = e.changedTouches[0];
          if (isZoomed) {
            resetZoom();
          } else {
            zoomIn(touch.clientX, touch.clientY);
          }
        }
        lastTap = currentTime;
      });

      const startPan = (e) => {
        if (!isZoomed) return;

        isDragging = true;
        startX = (e.type.includes('mouse') ? e.clientX : e.touches[0].clientX) - translateX;
        startY = (e.type.includes('mouse') ? e.clientY : e.touches[0].clientY) - translateY;

        zoomContainer.style.cursor = 'grabbing';
        link.style.cursor = 'grabbing';
      };

      const pan = (e) => {
        if (!isDragging || !isZoomed) return;

        e.preventDefault();
        const clientX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
        const clientY = e.type.includes('mouse') ? e.clientY : e.touches[0].clientY;

        translateX = clientX - startX;
        translateY = clientY - startY;

        const maxTranslate = (scale - 1) * 300;
        translateX = Math.max(Math.min(translateX, maxTranslate), -maxTranslate);
        translateY = Math.max(Math.min(translateY, maxTranslate), -maxTranslate);

        updateTransform();
      };

      const endPan = () => {
        if (!isZoomed) return;
        isDragging = false;
        zoomContainer.style.cursor = 'zoom-out';
        link.style.cursor = 'zoom-out';
      };

      link.addEventListener('mousedown', startPan);
      document.addEventListener('mousemove', pan);
      document.addEventListener('mouseup', endPan);

      link.addEventListener('touchstart', startPan, { passive: true });
      link.addEventListener('touchmove', pan, { passive: false });
      link.addEventListener('touchend', endPan);

      splideInstance.on('move', () => {
        resetZoom();
      });

      link.addEventListener('wheel', (e) => {
        e.preventDefault();

        if (e.deltaY < 0) {
          if (!isZoomed) {
            loadHighResImage();
            zoomIn(e.clientX, e.clientY);
          } else if (scale < 4) {
            scale = Math.min(scale + 0.5, 4);
            updateTransform();
          }
        } else {
          if (scale > 1) {
            scale = Math.max(scale - 0.5, 1);
            if (scale === 1) {
              resetZoom();
            } else {
              updateTransform();
            }
          }
        }
      }, { passive: false });

      link.addEventListener('mouseenter', () => {
        if (!highResLoaded) {
          loadHighResImage();
        }
      }, { once: true });
    });
  }
}

if (!customElements.get('custom-modal-opener')) {
  customElements.define('custom-modal-opener', CustomMediaPopupOpener);
}

if (!customElements.get('custom-modal-dialog')) {
  customElements.define('custom-modal-dialog', class extends HTMLElement { });
}
